import { useState, useRef, useCallback } from 'react'
import { LoginPage } from './components/layout/LoginPage'
import { MainLayout } from './components/layout/MainLayout'
import { HomePage } from './components/home/HomePage'
import { ReportBuilder } from './components/report/ReportBuilder'
import { MySpace } from './components/myspace/MySpace'
import { CatalogDetailPage } from './components/catalog/CatalogDetailPage'
import { SharedTemplates } from './components/templates/SharedTemplates'
import { TopicsPage } from './components/topics/TopicsPage'
import { CasesPage } from './components/cases/CasesPage'
import { CaseDetailPage } from './components/cases/CaseDetailPage'
import { UserCenterPage } from './components/user-center/UserCenterPage'
import { AdminCenterPage } from './components/admin-center/AdminCenterPage'
import { ASSET_DATA } from './data/assets'
import type { DataAsset } from './types'
import { REPORT_FIELDS } from './components/report/ReportBuilder'
import type { FilterCondition, SaveReportParams } from './components/report/ReportBuilder'
import { PAYROLL_REPORT_FIELDS } from './data/payroll-report'
import { CORP_BRANCH_REPORT_FIELDS, CORP_BRANCH_CORP_FIELDS, CORP_BRANCH_PERSONAL_FIELDS } from './data/corp-branch-deposit-report'
import { INCLUSIVE_LOAN_FIELDS } from './data/inclusive-loan-report'
import { CORP_CUSTOMER_FIELDS } from './data/corp-customer-report'
import { CORP_NEW_CUSTOMER_FIELDS } from './data/corp-new-customer-report'
import { PERSONAL_CUSTOMER_DETAIL_FIELDS } from './data/personal-customer-detail-report'
import { SHARED_TEMPLATES } from './data/shared-templates'
import { getUserBizRole, getUserBranch, getSubBranchNames } from './utils/user-role'
import type { UserBizRole } from './utils/user-role'
import { INSTITUTION_TREE } from './data/shared-templates'

type MenuPage = 'home' | 'analysis' | 'templates' | 'cases' | 'caseDetail' | 'myspace' | 'catalog' | 'report' | 'topics' | 'user-center' | 'admin-center'

interface Tab {
  id: string
  label: string
  menu: MenuPage
  closable: boolean
}

const TAB_PAGES: { menu: MenuPage; label: string }[] = [
  { menu: 'home', label: '首页' },
  { menu: 'topics', label: '业务主题库' },
  { menu: 'templates', label: '报表模板库' },
  { menu: 'cases', label: '经典案例' },
  { menu: 'myspace', label: '我的空间' },
  { menu: 'caseDetail', label: '案例详情' },
  { menu: 'user-center', label: '用户中心' },
  { menu: 'admin-center', label: '服务中心' },
]

// 对公大额存款变动情况 预设
const TEMPLATE_PRESET_FIELDS = ['d1', 'f1', 'f3', 'f6', 'f7', 'f5', 'f11', 'f13']
const TEMPLATE_PRESET_FILTER: FilterCondition[] = [
  {
    id: 'filter-template-date',
    field: REPORT_FIELDS.find(f => f.id === 'd1')!,
    operator: 'eq',
    value: '2026-06-10',
  },
  {
    id: 'filter-template-default',
    field: REPORT_FIELDS.find(f => f.id === 'f7')!,
    operator: 'lte',
    value: '-1000',
  },
]

// 代发客群营销分析 预设
const PAYROLL_PRESET_FIELDS: string[] = ['c2', 'c8', 'c9']
const PAYROLL_PRESET_FILTER: FilterCondition[] = [
  {
    id: 'filter-payroll-month',
    field: PAYROLL_REPORT_FIELDS.find(f => f.id === 'c11')!,
    operator: 'eq',
    value: '2026-05',
  },
  {
    id: 'filter-payroll-balance',
    field: PAYROLL_REPORT_FIELDS.find(f => f.id === 'c14')!,
    operator: 'lte',
    value: '10000',
  },
]
/** 代发客群透视分析默认计算字段：c12汇总值、c13汇总值 */
const PAYROLL_DEFAULT_COMPUTED_MEASURES = [
  { sourceFieldId: 'c12', aggType: 'sum' as const },
  { sourceFieldId: 'c13', aggType: 'sum' as const },
]
/** 代发客群默认自定义计算字段：转出比例 = c13_sum / c12_sum */
const PAYROLL_PRESET_CALCULATED_FIELDS = [
  {
    id: 'calc-chuzhuan-bili',
    name: '转出比例',
    expression: [
      { type: 'field' as const, fieldId: 'c13', aggType: 'sum' as const },
      { type: 'operator' as const, value: '/' as const },
      { type: 'field' as const, fieldId: 'c12', aggType: 'sum' as const },
    ],
  },
]

// 各机构存款情况统计 预设
const CORP_BRANCH_PRESET_FIELDS = ['d1', 'd2']
const CORP_BRANCH_PRESET_FILTER: FilterCondition[] = [
  {
    id: 'filter-corp-branch-date',
    field: CORP_BRANCH_REPORT_FIELDS.find(f => f.id === 'd1')!,
    operator: 'eq',
    value: '2026-06-10',
  },
]
const CORP_BRANCH_DEFAULT_COMPUTED_MEASURES = [
  { sourceFieldId: 'm1', aggType: 'sum' as const },
  { sourceFieldId: 'm1', aggType: 'vsPrevDay' as const },
  { sourceFieldId: 'm1', aggType: 'vsPrevMonth' as const },
  { sourceFieldId: 'm1', aggType: 'vsYearStart' as const },
  { sourceFieldId: 'm2', aggType: 'sum' as const },
  { sourceFieldId: 'm2', aggType: 'vsPrevDay' as const },
  { sourceFieldId: 'm2', aggType: 'vsPrevMonth' as const },
  { sourceFieldId: 'm2', aggType: 'vsYearStart' as const },
  { sourceFieldId: 'm3', aggType: 'sum' as const },
  { sourceFieldId: 'm3', aggType: 'vsPrevDay' as const },
  { sourceFieldId: 'm3', aggType: 'vsPrevMonth' as const },
  { sourceFieldId: 'm3', aggType: 'vsYearStart' as const },
  { sourceFieldId: 'm4', aggType: 'sum' as const },
  { sourceFieldId: 'm4', aggType: 'vsPrevDay' as const },
  { sourceFieldId: 'm4', aggType: 'vsPrevMonth' as const },
  { sourceFieldId: 'm4', aggType: 'vsYearStart' as const },
]

// 一年期及以下对公存款日均增量 预设（一点一亿场景）
const CORP_BRANCH_CORP_PRESET_FIELDS = ['d1', 'd2', 'cm-m1-sum', 'cm-m1-vsYearStart', 'cm-m3-sum', 'cm-m3-vsYearStart', 'cm-m9-sum', 'cm-m9-vsYearStart']
const CORP_BRANCH_CORP_PRESET_FILTER: FilterCondition[] = [
  {
    id: 'filter-corp-branch-corp-date',
    field: CORP_BRANCH_CORP_FIELDS.find(f => f.id === 'd1')!,
    operator: 'eq',
    value: '2026-06-10',
  },
  {
    id: 'filter-corp-branch-corp-m9-vsYearStart',
    field: { id: 'cm-m9-vsYearStart', name: '一年期及以下存款日均_比年初', type: 'measure' as const, group: '存款指标', dataType: '万元' },
    operator: 'gte',
    value: '10,000',
  },
]
const CORP_BRANCH_CORP_DEFAULT_COMPUTED_MEASURES = [
  { sourceFieldId: 'm1', aggType: 'sum' as const },
  { sourceFieldId: 'm1', aggType: 'vsYearStart' as const },
  { sourceFieldId: 'm3', aggType: 'sum' as const },
  { sourceFieldId: 'm3', aggType: 'vsYearStart' as const },
  { sourceFieldId: 'm9', aggType: 'sum' as const },
  { sourceFieldId: 'm9', aggType: 'vsYearStart' as const },
]

// 一年期及以下个人存款日均增量 预设（仅个人字段）
const CORP_BRANCH_PERSONAL_PRESET_FIELDS = ['d1', 'd2']
const CORP_BRANCH_PERSONAL_PRESET_FILTER: FilterCondition[] = [
  {
    id: 'filter-corp-branch-personal-date',
    field: CORP_BRANCH_PERSONAL_FIELDS.find(f => f.id === 'd1')!,
    operator: 'eq',
    value: '2026-06-10',
  },
]
const CORP_BRANCH_PERSONAL_DEFAULT_COMPUTED_MEASURES = [
  { sourceFieldId: 'm2', aggType: 'sum' as const },
  { sourceFieldId: 'm2', aggType: 'vsPrevDay' as const },
  { sourceFieldId: 'm2', aggType: 'vsPrevMonth' as const },
  { sourceFieldId: 'm2', aggType: 'vsYearStart' as const },
  { sourceFieldId: 'm4', aggType: 'sum' as const },
  { sourceFieldId: 'm4', aggType: 'vsPrevDay' as const },
  { sourceFieldId: 'm4', aggType: 'vsPrevMonth' as const },
  { sourceFieldId: 'm4', aggType: 'vsYearStart' as const },
]

// 普惠贷款增量 预设
const INCLUSIVE_LOAN_PRESET_FIELDS = ['data_date', 'customer_no', 'loan_no', 'customer_name', 'loan_amount', 'loan_balance', 'single_balance', 'branch_name', 'loan_type', 'term']
const INCLUSIVE_LOAN_PRESET_FILTER: FilterCondition[] = [
  {
    id: 'filter-inclusive-loan-date',
    field: INCLUSIVE_LOAN_FIELDS.find(f => f.id === 'data_date')!,
    operator: 'eq',
    value: '2026-06-10',
  },
]

// 公司价值户净增 预设（透视分析模式）
const CORP_CUSTOMER_PRESET_FIELDS = ['data_date', 'branch_name', 'cm-value_household-vsYearStart', 'cm-high_value_household-vsYearStart', 'cm-core_household-vsYearStart', 'cm-value_and_above-vsYearStart']
const CORP_CUSTOMER_DEFAULT_COMPUTED_MEASURES = [
  { sourceFieldId: 'value_household', aggType: 'vsYearStart' as const },
  { sourceFieldId: 'high_value_household', aggType: 'vsYearStart' as const },
  { sourceFieldId: 'core_household', aggType: 'vsYearStart' as const },
  { sourceFieldId: 'value_and_above', aggType: 'vsYearStart' as const },
]
const CORP_CUSTOMER_PRESET_FILTER: FilterCondition[] = [
  {
    id: 'filter-corp-customer-date',
    field: CORP_CUSTOMER_FIELDS.find(f => f.id === 'data_date')!,
    operator: 'eq',
    value: '2026-06-10',
  },
  {
    id: 'filter-corp-customer-value-vsYearStart',
    field: { id: 'cm-value_and_above-vsYearStart', name: '价值户及以上客户_比年初', type: 'measure' as const, group: '客户统计', dataType: '户' },
    operator: 'gte',
    value: '9',
  },
]

// 对公新开客户转换情况 预设
const CORP_NEW_CUSTOMER_PRESET_FIELDS = ['data_date', 'branch_name', 'week_new_accounts', 'week_to_basic', 'week_to_value', 'week_to_high_value', 'week_to_core', 'month_new_accounts', 'month_to_basic', 'month_to_value', 'month_to_high_value', 'month_to_core', 'year_new_accounts', 'year_to_basic', 'year_to_value', 'year_to_high_value', 'year_to_core']
const CORP_NEW_CUSTOMER_PRESET_FILTER: FilterCondition[] = [
  {
    id: 'filter-corp-new-customer-date',
    field: CORP_NEW_CUSTOMER_FIELDS.find(f => f.id === 'data_date')!,
    operator: 'eq',
    value: '2026-06-10',
  },
]

// 个人基础及以上客户净增明细 预设
const PERSONAL_CUSTOMER_DETAIL_PRESET_FIELDS = ['data_date', 'customer_name', 'branch_name', 'open_date', 'aum_90d', 'current_level', 'last_week_level', 'last_month_level', 'last_quarter_level', 'year_start_level']
const PERSONAL_CUSTOMER_DETAIL_PRESET_FILTER: FilterCondition[] = [
  {
    id: 'filter-personal-customer-date',
    field: PERSONAL_CUSTOMER_DETAIL_FIELDS.find(f => f.id === 'data_date')!,
    operator: 'eq',
    value: '2026-06-10',
  },
]

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [currentUser, setCurrentUser] = useState('')
  const [appVersion, setAppVersion] = useState('1')
  const [currentMenu, setCurrentMenu] = useState<MenuPage>('home')
  const [tabs, setTabs] = useState<Tab[]>([
    { id: 'home', label: '首页', menu: 'home', closable: false },
  ])
  const [selectedAsset, setSelectedAsset] = useState<DataAsset | null>(null)
  const [selectedCatalogDetailId, setSelectedCatalogDetailId] = useState<string | undefined>(undefined)
  const [selectedCaseId, setSelectedCaseId] = useState<string | undefined>(undefined)
  const [caseSource, setCaseSource] = useState<'home' | 'cases'>('home')
  const [catalogAuthorized, setCatalogAuthorized] = useState<boolean>(false)
  const [reportPresetFields, setReportPresetFields] = useState<string[] | undefined>(undefined)
  const [reportPresetFilters, setReportPresetFilters] = useState<FilterCondition[] | undefined>(undefined)
  const [reportHiddenFilters, setReportHiddenFilters] = useState<FilterCondition[] | undefined>(undefined)
  const [reportPresetPivotMode, setReportPresetPivotMode] = useState<boolean>(false)
  /** 当前进入报表制作的模板 ID，用于区分使用哪套字段和数据 */
  const [activeReportTemplate, setActiveReportTemplate] = useState<string | null>(null)
  /** 预设计算字段（透视分析模式下默认勾选的聚合字段） */
  const [reportPresetComputedMeasures, setReportPresetComputedMeasures] = useState<Array<{ sourceFieldId: string; aggType: 'sum' | 'avg' | 'max' | 'min' }> | undefined>(undefined)
  /** 预设自定义计算字段（用于传入转出比例等表达式字段） */
  const [reportPresetCalculatedFields, setReportPresetCalculatedFields] = useState<Array<{ id: string; name: string; expression: any[] }> | undefined>(undefined)
  /** 报表渲染 key，每次进入报表页面时递增以强制重新挂载组件 */
  const [reportKey, setReportKey] = useState(0)

  /** 从"我的收藏"跳转时，各页面是否需要激活收藏筛选 */
  const [topicsShowFavorites, setTopicsShowFavorites] = useState(false)
  const [templatesShowFavorites, setTemplatesShowFavorites] = useState(false)
  const [casesShowFavorites, setCasesShowFavorites] = useState(false)

  // 首页搜索状态
  const [homeSearchMode, setHomeSearchMode] = useState(false)
  const [homeSearchKeyword, setHomeSearchKeyword] = useState('')
  const homeSearchModeRef = useRef(homeSearchMode)
  homeSearchModeRef.current = homeSearchMode


  /** 获取当前用户的业务角色 */
  const userBizRole: UserBizRole = getUserBizRole(currentUser)

  /** 获取支行过滤器：支行用户自动按支行过滤 */
  const getBranchFilter = (tid?: string): FilterCondition | null => {
    const branch = getUserBranch(currentUser)
    if (!branch) return null
    const fieldId = tid === '12' || tid === '12a' || tid === '12b' ? 'd2' : tid === '11' ? 'f1' : tid === '101' || tid === '102' || tid === '104' || tid === '106' ? 'branch_name' : 'f1'
    const getField = (templateId: string) => {
      if (templateId === '12' || templateId === '12a' || templateId === '12b') return CORP_BRANCH_REPORT_FIELDS.find(f => f.id === fieldId)!
      if (templateId === '11') return PAYROLL_REPORT_FIELDS.find(f => f.id === fieldId) || REPORT_FIELDS.find(f => f.id === fieldId)!
      if (templateId === '101') return INCLUSIVE_LOAN_FIELDS.find(f => f.id === fieldId)!
      if (templateId === '102') return CORP_CUSTOMER_FIELDS.find(f => f.id === fieldId)!
      if (templateId === '104') return CORP_NEW_CUSTOMER_FIELDS.find(f => f.id === fieldId)!
      if (templateId === '106') return PERSONAL_CUSTOMER_DETAIL_FIELDS.find(f => f.id === fieldId)!
      return REPORT_FIELDS.find(f => f.id === fieldId)!
    }
    return {
      id: `filter-branch-${branch}`,
      field: getField(tid || ''),
      operator: 'eq',
      value: branch,
    }
  }

  /** 获取支行隐藏过滤器（v1.0 支行用户自动按支行过滤数据，不在筛选区显示） */
  const getBranchHiddenFilter = (tid?: string): FilterCondition[] | undefined => {
    if (appVersion !== '1') return undefined
    const branch = getUserBranch(currentUser)
    if (!branch) return undefined
    const fieldId = tid === '12' || tid === '12a' || tid === '12b' ? 'd2' : tid === '101' || tid === '102' || tid === '104' || tid === '106' ? 'branch_name' : 'f1'
    const getField = (templateId: string) => {
      if (templateId === '12' || templateId === '12a' || templateId === '12b') return CORP_BRANCH_REPORT_FIELDS.find(f => f.id === fieldId)!
      if (templateId === '11') return PAYROLL_REPORT_FIELDS.find(f => f.id === fieldId) || REPORT_FIELDS.find(f => f.id === fieldId)!
      if (templateId === '101') return INCLUSIVE_LOAN_FIELDS.find(f => f.id === fieldId)!
      if (templateId === '102') return CORP_CUSTOMER_FIELDS.find(f => f.id === fieldId)!
      if (templateId === '104') return CORP_NEW_CUSTOMER_FIELDS.find(f => f.id === fieldId)!
      if (templateId === '106') return PERSONAL_CUSTOMER_DETAIL_FIELDS.find(f => f.id === fieldId)!
      return REPORT_FIELDS.find(f => f.id === fieldId)!
    }
    return [{
      id: `filter-branch-${branch}`,
      field: getField(tid || ''),
      operator: 'eq',
      value: branch,
    }]
  }

  const handleLogin = (username: string) => {
    setCurrentUser(username)
    setIsLoggedIn(true)
  }

  // Tab 管理：打开或激活一个已支持 Tab 的页面
  const openOrActivateTab = useCallback((menu: MenuPage, label: string) => {
    setTabs(prev => {
      const exists = prev.find(t => t.menu === menu)
      if (exists) return prev
      return [...prev, { id: menu, label, menu, closable: true }]
    })
    setCurrentMenu(menu)
  }, [])

  const handleNavigate = (menu: string) => {
    // 检查是否为可 Tab 页面
    const tabConfig = TAB_PAGES.find(t => t.menu === menu)
    if (tabConfig) {
      // 如果当前已是该页面且处于搜索模式，则重置搜索
      if (menu === 'home' && currentMenu === 'home' && homeSearchModeRef.current) {
        setHomeSearchMode(false)
        setHomeSearchKeyword('')
        return
      }
      // 从其他页面切换回首页时，也重置搜索状态
      if (menu === 'home' && homeSearchModeRef.current) {
        setHomeSearchMode(false)
        setHomeSearchKeyword('')
      }
      openOrActivateTab(tabConfig.menu, tabConfig.label)
      return
    }
    // 非 Tab 页面直接切换
    setCurrentMenu(menu as MenuPage)
  }

  // 关闭 Tab
  const handleCloseTab = (tabId: string) => {
    setTabs(prev => {
      const idx = prev.findIndex(t => t.id === tabId)
      if (idx === -1) return prev
      // 如果关闭的是案例详情 Tab，清除选中状态
      if (tabId === 'caseDetail') setSelectedCaseId(undefined)
      const newTabs = prev.filter(t => t.id !== tabId)
      // 如果关闭的是当前激活的 Tab，切换到相邻 Tab
      if (currentMenu === tabId && newTabs.length > 0) {
        const nextTab = newTabs[Math.min(idx, newTabs.length - 1)]
        setCurrentMenu(nextTab.menu)
      } else if (newTabs.length === 0) {
        // 最后一个 Tab 关闭后，默认打开首页
        setCurrentMenu('home')
        setTabs([{ id: 'home', label: '首页', menu: 'home', closable: false }])
      }
      return newTabs
    })
  }

  // 点击已有 Tab 切换
  const handleSwitchTab = (menu: string) => {
    setCurrentMenu(menu as MenuPage)
  }

  const handleHomeSearch = (keyword: string) => {
    setHomeSearchKeyword(keyword)
    setHomeSearchMode(true)
  }

  const handleHomeResetSearch = () => {
    setHomeSearchMode(false)
    setHomeSearchKeyword('')
  }

  const handleReportQuery = (asset: DataAsset, templateId?: string) => {
    setSelectedAsset(asset)
    setReportPresetFields([])
    setReportPresetFilters([])
    setReportPresetPivotMode(false)
    setActiveReportTemplate(templateId || null)
    setReportKey(k => k + 1)
    setCurrentMenu('report')
  }

  const handleNavigateToReport = (presetFieldIds?: string[], presetFilters?: FilterCondition[], pivotMode?: boolean, templateId?: string, presetComputedMeasures?: Array<{ sourceFieldId: string; aggType: 'sum' | 'avg' | 'max' | 'min' }>, presetCalculatedFields?: Array<{ id: string; name: string; expression: any[] }>) => {
    const hiddenFilter = getBranchHiddenFilter(templateId)
    // v1.0 支行用户使用隐藏筛选（不在筛选区显示），其他用户使用可见筛选
    const branchFilter = hiddenFilter ? undefined : getBranchFilter(templateId)
    const mergedFilters = branchFilter ? [...(presetFilters || []), branchFilter] : presetFilters
    setSelectedAsset(null)
    setReportPresetFields(presetFieldIds)
    setReportPresetFilters(mergedFilters)
    setReportHiddenFilters(hiddenFilter)
    setReportPresetPivotMode(pivotMode || false)
    setActiveReportTemplate(templateId || null)
    setReportPresetComputedMeasures(presetComputedMeasures)
    setReportPresetCalculatedFields(presetCalculatedFields)
    setReportKey(k => k + 1)
    setCurrentMenu('report')
  }

  /** 根据模板 ID 导航到报表制作页面（用于我的空间收藏点击等场景） */
  const handleNavigateToReportByTemplateId = (templateId?: string) => {
    if (!templateId) {
      setSelectedAsset(null)
      setReportPresetFields([])
      setReportPresetFilters([])
      setReportHiddenFilters(undefined)
      setReportPresetPivotMode(false)
      setActiveReportTemplate(null)
      setReportPresetComputedMeasures([])
      setReportPresetCalculatedFields([])
      setReportKey(k => k + 1)
      setCurrentMenu('report')
      return
    }
    if (templateId === '11') {
      handleNavigateToReport(PAYROLL_PRESET_FIELDS, PAYROLL_PRESET_FILTER, true, templateId, PAYROLL_DEFAULT_COMPUTED_MEASURES, PAYROLL_PRESET_CALCULATED_FIELDS)
    } else if (templateId === '12a') {
      handleNavigateToReport(CORP_BRANCH_CORP_PRESET_FIELDS, CORP_BRANCH_CORP_PRESET_FILTER, true, templateId, CORP_BRANCH_CORP_DEFAULT_COMPUTED_MEASURES)
    } else if (templateId === '12b') {
      handleNavigateToReport(CORP_BRANCH_PERSONAL_PRESET_FIELDS, CORP_BRANCH_PERSONAL_PRESET_FILTER, true, templateId, CORP_BRANCH_PERSONAL_DEFAULT_COMPUTED_MEASURES)
    } else if (templateId === '12') {
      handleNavigateToReport(CORP_BRANCH_PRESET_FIELDS, CORP_BRANCH_PRESET_FILTER, true, templateId, CORP_BRANCH_DEFAULT_COMPUTED_MEASURES)
    } else if (templateId === '101') {
      handleNavigateToReport(INCLUSIVE_LOAN_PRESET_FIELDS, INCLUSIVE_LOAN_PRESET_FILTER, false, templateId)
    } else if (templateId === '102') {
      handleNavigateToReport(CORP_CUSTOMER_PRESET_FIELDS, CORP_CUSTOMER_PRESET_FILTER, true, templateId, CORP_CUSTOMER_DEFAULT_COMPUTED_MEASURES)
    } else if (templateId === '104') {
      handleNavigateToReport(CORP_NEW_CUSTOMER_PRESET_FIELDS, CORP_NEW_CUSTOMER_PRESET_FILTER, false, templateId)
    } else if (templateId === '106') {
      handleNavigateToReport(PERSONAL_CUSTOMER_DETAIL_PRESET_FIELDS, PERSONAL_CUSTOMER_DETAIL_PRESET_FILTER, false, templateId)
    } else if (templateId === '1') {
      handleNavigateToReport(TEMPLATE_PRESET_FIELDS, TEMPLATE_PRESET_FILTER, false, templateId)
    } else {
      // 其他模板：不预设字段，使用全选
      const hiddenFilter = getBranchHiddenFilter(templateId)
      const branchFilter = hiddenFilter ? undefined : getBranchFilter(templateId)
      setSelectedAsset(null)
      setReportPresetFields(undefined)
      setReportPresetFilters(branchFilter ? [branchFilter] : undefined)
      setReportHiddenFilters(hiddenFilter)
      setReportPresetPivotMode(false)
      setActiveReportTemplate(templateId)
      setReportPresetComputedMeasures([])
      setReportPresetCalculatedFields([])
      setReportKey(k => k + 1)
      setCurrentMenu('report')
    }
  }

  const handleLearnMore = (catalogId: string) => {
    setSelectedCatalogDetailId(catalogId)
    const asset = ASSET_DATA.find(a => a.catalogId === catalogId)
    setCatalogAuthorized(asset?.authorized || false)
    setCurrentMenu('catalog')
  }

  /** 保存报表到 localStorage（我的空间可读取） */
  const handleSaveReport = (params: SaveReportParams) => {
    try {
      const key = 'saved_reports'
      const existing = JSON.parse(localStorage.getItem(key) || '[]')
      const now = new Date()
      const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
      const newReport = {
        id: `saved-${Date.now()}`,
        name: params.name,
        description: params.description,
        createdAt: dateStr,
        updatedAt: dateStr,
        viewCount: 0,
        source: displayTopicNameForReport() || '自定义报表',
        categoryId: params.categoryId,
        config: params.config,
        createdBy: currentUser,
      }
      existing.push(newReport)
      localStorage.setItem(key, JSON.stringify(existing))
    } catch (e) {
      console.error('保存报表失败:', e)
    }
  }

  /** 获取当前报表主题名（用于保存时的数据源显示） */
  function displayTopicNameForReport() {
    if (selectedAsset?.name) return selectedAsset.name
    if (activeReportTemplate) {
      const template = SHARED_TEMPLATES.find(t => t.id === activeReportTemplate)
      if (template) return template.name
    }
    return null
  }

  const handleBackFromCatalog = () => {
    setSelectedCatalogDetailId(undefined)
    // 返回首页 Tab
    const homeTab = tabs.find(t => t.menu === 'home')
    if (homeTab) {
      setCurrentMenu('home')
    } else {
      openOrActivateTab('home', '首页')
    }
    setHomeSearchMode(false)
    setHomeSearchKeyword('')
  }

  /** 打开"我的空间"中已保存的报表 */
  const handleOpenReport = (reportId: string) => {
    if (reportId === '1') {
      handleNavigateToReport(TEMPLATE_PRESET_FIELDS, TEMPLATE_PRESET_FILTER, false, '1')
    } else if (reportId === '1b') {
      handleNavigateToReport(TEMPLATE_PRESET_FIELDS, [
        ...TEMPLATE_PRESET_FILTER,
        {
          id: 'filter-balance',
          field: REPORT_FIELDS.find(f => f.id === 'f6')!,
          operator: 'gte',
          value: '1000',
        },
      ], false, '1')
    } else if (reportId === '2') {
      handleNavigateToReport(CORP_BRANCH_CORP_PRESET_FIELDS, CORP_BRANCH_CORP_PRESET_FILTER, true, '12a', CORP_BRANCH_CORP_DEFAULT_COMPUTED_MEASURES)
    } else if (reportId === '3') {
      handleNavigateToReport(CORP_BRANCH_PERSONAL_PRESET_FIELDS, CORP_BRANCH_PERSONAL_PRESET_FILTER, true, '12b', CORP_BRANCH_PERSONAL_DEFAULT_COMPUTED_MEASURES)
    } else if (reportId === '101') {
      handleNavigateToReport(INCLUSIVE_LOAN_PRESET_FIELDS, INCLUSIVE_LOAN_PRESET_FILTER, false, '101')
    } else if (reportId === '102') {
      handleNavigateToReport(CORP_CUSTOMER_PRESET_FIELDS, CORP_CUSTOMER_PRESET_FILTER, true, '102', CORP_CUSTOMER_DEFAULT_COMPUTED_MEASURES)
    } else if (reportId === '104') {
      handleNavigateToReport(CORP_NEW_CUSTOMER_PRESET_FIELDS, CORP_NEW_CUSTOMER_PRESET_FILTER, false, '104')
    } else if (reportId === '106') {
      handleNavigateToReport(PERSONAL_CUSTOMER_DETAIL_PRESET_FIELDS, PERSONAL_CUSTOMER_DETAIL_PRESET_FILTER, false, '106')
    } else {
      // 从 localStorage 读取保存的报表配置
      try {
        const saved = JSON.parse(localStorage.getItem('saved_reports') || '[]')
        const report = saved.find((r: any) => r.id === reportId)
        if (report?.config) {
          // v1.0 支行用户使用隐藏筛选，不显示在筛选区
          const hiddenFilter = getBranchHiddenFilter(report.config.activeReportTemplate)
          if (hiddenFilter) {
            setReportHiddenFilters(hiddenFilter)
            setSelectedAsset(null)
            setReportPresetFields(report.config.selectedFields || [])
            setReportPresetFilters(report.config.filters || [])
          } else {
            // 非支行用户使用可见筛选
            const branchFilter = getBranchFilter(report.config.activeReportTemplate)
            const existingFilters = report.config.filters || []
            const hasBranchFilter = branchFilter && existingFilters.some((f: any) => f.operator === 'eq' && (f.value === branchFilter.value || f.field?.name === '支行名称'))
            const mergedFilters = (branchFilter && !hasBranchFilter) ? [...existingFilters, branchFilter] : existingFilters
            setReportHiddenFilters(undefined)
            setSelectedAsset(null)
            setReportPresetFields(report.config.selectedFields || [])
            setReportPresetFilters(mergedFilters)
          }
          setReportPresetPivotMode(report.config.isPivotMode || false)
          setActiveReportTemplate(report.config.activeReportTemplate || null)
          setReportPresetComputedMeasures(report.config.computedMeasures || [])
          setReportPresetCalculatedFields(report.config.calculatedFields || [])
          setReportKey(k => k + 1)
          setCurrentMenu('report')
          return
        }
      } catch (e) {
        console.error('打开报表失败:', e)
      }
      setCurrentMenu('report')
    }
  }

  const handleCreateNew = () => {
    setSelectedAsset(null)
    setCurrentMenu('report')
  }

  const handleEditReport = (reportId: string) => {
    if (reportId === '1') {
      handleNavigateToReport(TEMPLATE_PRESET_FIELDS, TEMPLATE_PRESET_FILTER, false, '1')
    } else if (reportId === '1b') {
      handleNavigateToReport(TEMPLATE_PRESET_FIELDS, [
        ...TEMPLATE_PRESET_FILTER,
        {
          id: 'filter-balance',
          field: REPORT_FIELDS.find(f => f.id === 'f6')!,
          operator: 'gte',
          value: '1000',
        },
      ], false, '1')
    } else if (reportId === '2') {
      handleNavigateToReport(CORP_BRANCH_CORP_PRESET_FIELDS, CORP_BRANCH_CORP_PRESET_FILTER, true, '12a', CORP_BRANCH_CORP_DEFAULT_COMPUTED_MEASURES)
    } else if (reportId === '3') {
      handleNavigateToReport(CORP_BRANCH_PERSONAL_PRESET_FIELDS, CORP_BRANCH_PERSONAL_PRESET_FILTER, true, '12b', CORP_BRANCH_PERSONAL_DEFAULT_COMPUTED_MEASURES)
    } else if (reportId === '101') {
      handleNavigateToReport(INCLUSIVE_LOAN_PRESET_FIELDS, INCLUSIVE_LOAN_PRESET_FILTER, false, '101')
    } else if (reportId === '102') {
      handleNavigateToReport(CORP_CUSTOMER_PRESET_FIELDS, CORP_CUSTOMER_PRESET_FILTER, true, '102', CORP_CUSTOMER_DEFAULT_COMPUTED_MEASURES)
    } else if (reportId === '104') {
      handleNavigateToReport(CORP_NEW_CUSTOMER_PRESET_FIELDS, CORP_NEW_CUSTOMER_PRESET_FILTER, false, '104')
    } else if (reportId === '106') {
      handleNavigateToReport(PERSONAL_CUSTOMER_DETAIL_PRESET_FIELDS, PERSONAL_CUSTOMER_DETAIL_PRESET_FILTER, false, '106')
    } else {
      // 从 localStorage 读取保存的报表配置
      try {
        const saved = JSON.parse(localStorage.getItem('saved_reports') || '[]')
        const report = saved.find((r: any) => r.id === reportId)
        if (report?.config) {
          setSelectedAsset(null)
          setReportPresetFields(report.config.selectedFields || [])
          setReportPresetFilters(report.config.filters || [])
          setReportPresetPivotMode(report.config.isPivotMode || false)
          setActiveReportTemplate(report.config.activeReportTemplate || null)
          setReportPresetComputedMeasures(report.config.computedMeasures || [])
          setReportPresetCalculatedFields(report.config.calculatedFields || [])
          setReportKey(k => k + 1)
          setCurrentMenu('report')
          return
        }
      } catch (e) {
        console.error('编辑报表失败:', e)
      }
      setCurrentMenu('report')
    }
  }

  /** 查看共享模板（从"我的空间"共享到共享模板的报表） */
  const handleViewSharedTemplate = (templateId?: string) => {
    if (!templateId) {
      setCurrentMenu('report')
      return
    }
    // 如果是内置模板 ID，使用原有逻辑
    if (templateId === '1' || templateId === '11' || templateId === '12' || templateId === '12a' || templateId === '12b' || templateId === '101' || templateId === '102' || templateId === '104' || templateId === '106') {
      if (templateId === '11') {
        handleNavigateToReport(PAYROLL_PRESET_FIELDS, PAYROLL_PRESET_FILTER, true, templateId, PAYROLL_DEFAULT_COMPUTED_MEASURES, PAYROLL_PRESET_CALCULATED_FIELDS)
      } else if (templateId === '12a') {
        handleNavigateToReport(CORP_BRANCH_CORP_PRESET_FIELDS, CORP_BRANCH_CORP_PRESET_FILTER, true, templateId, CORP_BRANCH_CORP_DEFAULT_COMPUTED_MEASURES)
      } else if (templateId === '12b') {
        handleNavigateToReport(CORP_BRANCH_PERSONAL_PRESET_FIELDS, CORP_BRANCH_PERSONAL_PRESET_FILTER, true, templateId, CORP_BRANCH_PERSONAL_DEFAULT_COMPUTED_MEASURES)
      } else if (templateId === '12') {
        handleNavigateToReport(CORP_BRANCH_PRESET_FIELDS, CORP_BRANCH_PRESET_FILTER, true, templateId, CORP_BRANCH_DEFAULT_COMPUTED_MEASURES)
      } else if (templateId === '101') {
        handleNavigateToReport(INCLUSIVE_LOAN_PRESET_FIELDS, INCLUSIVE_LOAN_PRESET_FILTER, false, templateId)
      } else if (templateId === '102') {
        handleNavigateToReport(CORP_CUSTOMER_PRESET_FIELDS, CORP_CUSTOMER_PRESET_FILTER, true, templateId, CORP_CUSTOMER_DEFAULT_COMPUTED_MEASURES)
      } else if (templateId === '104') {
        handleNavigateToReport(CORP_NEW_CUSTOMER_PRESET_FIELDS, CORP_NEW_CUSTOMER_PRESET_FILTER, false, templateId)
      } else if (templateId === '106') {
        handleNavigateToReport(PERSONAL_CUSTOMER_DETAIL_PRESET_FIELDS, PERSONAL_CUSTOMER_DETAIL_PRESET_FILTER, false, templateId)
      } else {
        handleNavigateToReport(TEMPLATE_PRESET_FIELDS, TEMPLATE_PRESET_FILTER, false, templateId)
      }
      return
    }
    // 用户共享的报表（ID 格式：shared-xxx），从 localStorage 恢复配置
    try {
      const shared = JSON.parse(localStorage.getItem('shared_reports') || '[]')
      const record = shared.find((r: any) => r.id === templateId)
      if (record?.config) {
        setSelectedAsset(null)
        setReportPresetFields(record.config.selectedFields || [])
        setReportPresetFilters(record.config.filters || [])
        setReportPresetPivotMode(record.config.isPivotMode || false)
        setActiveReportTemplate(record.config.activeReportTemplate || null)
        setReportPresetComputedMeasures(record.config.computedMeasures || [])
        setReportPresetCalculatedFields(record.config.calculatedFields || [])
        setReportKey(k => k + 1)
        setCurrentMenu('report')
        return
      }
    } catch (e) {
      console.error('查看共享模板失败:', e)
    }
    setCurrentMenu('report')
  }

  const handleCaseDetail = (caseId: string) => {
    setSelectedCaseId(caseId)
    setCaseSource('home')
    openOrActivateTab('caseDetail', '案例详情')
  }

  const handleCaseDetailFromCases = (caseId: string) => {
    setSelectedCaseId(caseId)
    setCaseSource('cases')
    openOrActivateTab('caseDetail', '案例详情')
  }

  const handleBackFromCaseDetail = () => {
    setSelectedCaseId(undefined)
    const targetMenu = caseSource === 'cases' ? 'cases' : 'home'
    // 关闭案例详情 Tab
    setTabs(prev => {
      const newTabs = prev.filter(t => t.menu !== 'caseDetail')
      if (newTabs.length === 0) {
        return [{ id: 'home', label: '首页', menu: 'home', closable: false }]
      }
      return newTabs
    })
    setCurrentMenu(targetMenu)
  }

  const handleCaseTemplateLink = (templateId: string) => {
    setSelectedCaseId(undefined)
    if (templateId === '11') {
      handleNavigateToReport(PAYROLL_PRESET_FIELDS, PAYROLL_PRESET_FILTER, true, templateId, PAYROLL_DEFAULT_COMPUTED_MEASURES, PAYROLL_PRESET_CALCULATED_FIELDS)
    } else if (templateId === '1') {
      handleNavigateToReport(TEMPLATE_PRESET_FIELDS, TEMPLATE_PRESET_FILTER, false, templateId)
    } else if (templateId === '101') {
      handleNavigateToReport(INCLUSIVE_LOAN_PRESET_FIELDS, INCLUSIVE_LOAN_PRESET_FILTER, false, templateId)
    } else if (templateId === '102') {
      handleNavigateToReport(CORP_CUSTOMER_PRESET_FIELDS, CORP_CUSTOMER_PRESET_FILTER, true, templateId, CORP_CUSTOMER_DEFAULT_COMPUTED_MEASURES)
    } else if (templateId === '104') {
      handleNavigateToReport(CORP_NEW_CUSTOMER_PRESET_FIELDS, CORP_NEW_CUSTOMER_PRESET_FILTER, false, templateId)
    } else if (templateId === '106') {
      handleNavigateToReport(PERSONAL_CUSTOMER_DETAIL_PRESET_FIELDS, PERSONAL_CUSTOMER_DETAIL_PRESET_FILTER, false, templateId)
    } else if (templateId === '12' || templateId === '12a' || templateId === '12b') {
      if (templateId === '12a') {
        handleNavigateToReport(CORP_BRANCH_CORP_PRESET_FIELDS, CORP_BRANCH_CORP_PRESET_FILTER, true, templateId, CORP_BRANCH_CORP_DEFAULT_COMPUTED_MEASURES)
      } else if (templateId === '12b') {
        handleNavigateToReport(CORP_BRANCH_PERSONAL_PRESET_FIELDS, CORP_BRANCH_PERSONAL_PRESET_FILTER, true, templateId, CORP_BRANCH_PERSONAL_DEFAULT_COMPUTED_MEASURES)
      } else {
        handleNavigateToReport(CORP_BRANCH_PRESET_FIELDS, CORP_BRANCH_PRESET_FILTER, true, templateId, CORP_BRANCH_DEFAULT_COMPUTED_MEASURES)
      }
    } else {
      openOrActivateTab('templates', '报表模板库')
    }
  }

  const handlePayrollTopicAnalysis = () => {
    // 代发客群资金交易明细主题：进入代发报表制作页面，默认进入透视分析模式
    setSelectedAsset(null)
    setReportPresetFields(['c2', 'c8', 'c9'])
    setReportPresetFilters(PAYROLL_PRESET_FILTER)
    setReportPresetPivotMode(true)
    setActiveReportTemplate('11')
    setReportPresetComputedMeasures(PAYROLL_DEFAULT_COMPUTED_MEASURES)
    setReportPresetCalculatedFields(PAYROLL_PRESET_CALCULATED_FIELDS)
    setReportKey(k => k + 1)
    setCurrentMenu('report')
  }

  /** 从"我的收藏"跳转到业务主题，激活收藏筛选 */
  const handleNavToFavoritesTopics = () => {
    setTemplatesShowFavorites(false)
    setCasesShowFavorites(false)
    setTopicsShowFavorites(true)
    openOrActivateTab('topics', '业务主题库')
  }

  /** 从"我的收藏"跳转到共享模板，激活收藏筛选 */
  const handleNavToFavoritesTemplates = () => {
    setTopicsShowFavorites(false)
    setCasesShowFavorites(false)
    setTemplatesShowFavorites(true)
    openOrActivateTab('templates', '共享模板')
  }

  /** 从"我的收藏"跳转到经典案例，激活收藏筛选 */
  const handleNavToFavoritesCases = () => {
    setTopicsShowFavorites(false)
    setTemplatesShowFavorites(false)
    setCasesShowFavorites(true)
    openOrActivateTab('cases', '经典案例')
  }

  const handleTopicSelfAnalysis = (topicId: string) => {
    // 根据主题ID进入对应报表制作页面，不勾选字段
    const mapping: Record<string, { template: string; fields: string[]; filters: FilterCondition[]; pivot: boolean }> = {
      'topic-payroll': { template: '11', fields: [], filters: [], pivot: false },
      'topic-corp-deposit': { template: '1', fields: [], filters: [], pivot: false },
      'topic-corp-branch-deposit': { template: '12a', fields: [], filters: [], pivot: false },
    }
    const config = mapping[topicId]
    if (config) {
      handleNavigateToReport(config.fields, config.filters, config.pivot, config.template)
    } else {
      setSelectedAsset(null)
      setReportPresetFields([])
      setReportPresetFilters([])
      setReportPresetPivotMode(false)
      setActiveReportTemplate(null)
      setCurrentMenu('report')
    }
  }

  if (!isLoggedIn) {
    return <LoginPage onLogin={handleLogin} version={appVersion} onVersionChange={setAppVersion} />
  }

  const showSidebar = currentMenu === 'home' || currentMenu === 'topics' || currentMenu === 'templates' || currentMenu === 'cases' || currentMenu === 'myspace' || currentMenu === 'caseDetail' || currentMenu === 'user-center' || currentMenu === 'admin-center'

  return (
    <MainLayout
      currentMenu={currentMenu}
      onNavigate={handleNavigate}
      showSidebar={showSidebar}
      version={appVersion}
      currentUser={currentUser}
      userBizRole={userBizRole}
      tabs={tabs}
      activeTab={currentMenu}
      onSwitchTab={handleSwitchTab}
      onCloseTab={handleCloseTab}
    >
      {currentMenu === 'home' ? (
        <HomePage
          searchMode={homeSearchMode}
          searchKeyword={homeSearchKeyword}
          onSearch={handleHomeSearch}
          onResetSearch={handleHomeResetSearch}
          onLearnMore={handleLearnMore}
          onCaseDetail={handleCaseDetail}
          onTemplateNavigate={() => openOrActivateTab('templates', '共享模板')}
          onCasesNavigate={() => openOrActivateTab('cases', '经典案例')}
          onNavigate={handleNavigate}
          onViewReport={(templateId: string | undefined) => {
            const tid = templateId || '1'
            if (tid === '11') {
              handleNavigateToReport(PAYROLL_PRESET_FIELDS, PAYROLL_PRESET_FILTER, true, tid, PAYROLL_DEFAULT_COMPUTED_MEASURES, PAYROLL_PRESET_CALCULATED_FIELDS)
            } else if (tid === '12a') {
              handleNavigateToReport(CORP_BRANCH_CORP_PRESET_FIELDS, CORP_BRANCH_CORP_PRESET_FILTER, true, tid, CORP_BRANCH_CORP_DEFAULT_COMPUTED_MEASURES)
            } else if (tid === '12b') {
              handleNavigateToReport(CORP_BRANCH_PERSONAL_PRESET_FIELDS, CORP_BRANCH_PERSONAL_PRESET_FILTER, true, tid, CORP_BRANCH_PERSONAL_DEFAULT_COMPUTED_MEASURES)
            } else if (tid === '12') {
              handleNavigateToReport(CORP_BRANCH_PRESET_FIELDS, CORP_BRANCH_PRESET_FILTER, true, tid, CORP_BRANCH_DEFAULT_COMPUTED_MEASURES)
            } else if (tid === '101') {
              handleNavigateToReport(INCLUSIVE_LOAN_PRESET_FIELDS, INCLUSIVE_LOAN_PRESET_FILTER, false, tid)
            } else if (tid === '102') {
              handleNavigateToReport(CORP_CUSTOMER_PRESET_FIELDS, CORP_CUSTOMER_PRESET_FILTER, true, tid, CORP_CUSTOMER_DEFAULT_COMPUTED_MEASURES)
            } else if (tid === '104') {
              handleNavigateToReport(CORP_NEW_CUSTOMER_PRESET_FIELDS, CORP_NEW_CUSTOMER_PRESET_FILTER, false, tid)
            } else if (tid === '106') {
              handleNavigateToReport(PERSONAL_CUSTOMER_DETAIL_PRESET_FIELDS, PERSONAL_CUSTOMER_DETAIL_PRESET_FILTER, false, tid)
            } else {
              handleNavigateToReport(TEMPLATE_PRESET_FIELDS, TEMPLATE_PRESET_FILTER, false, tid)
            }
          }}
          onPayrollTopicAnalysis={handlePayrollTopicAnalysis}
          onSelfAnalysis={handleTopicSelfAnalysis}
          version={appVersion}
          currentUser={currentUser}
          userBizRole={userBizRole}
        />
      ) : currentMenu === 'topics' ? (
        <TopicsPage onLearnMore={handleLearnMore} onSelfAnalysis={handleTopicSelfAnalysis} showFavoritesOnly={topicsShowFavorites} version={appVersion} />
      ) : currentMenu === 'cases' ? (
        <CasesPage onCaseDetail={handleCaseDetailFromCases} showFavoritesOnly={casesShowFavorites} />
      ) : currentMenu === 'caseDetail' ? (
        <CaseDetailPage caseId={selectedCaseId || ''} onBack={handleBackFromCaseDetail} onTemplateLink={handleCaseTemplateLink} />
      ) : currentMenu === 'templates' ? (
        <SharedTemplates
          onViewReport={handleViewSharedTemplate}
          showFavoritesOnly={templatesShowFavorites}
          version={appVersion}
          userBizRole={userBizRole}
        />
      ) : currentMenu === 'report' ? (
        <ReportBuilder
          key={reportKey}
          asset={selectedAsset}
          presetFields={reportPresetFields}
          presetFilters={reportPresetFilters}
          hiddenFilters={reportHiddenFilters}
          presetPivotMode={reportPresetPivotMode}
          activeReportTemplate={activeReportTemplate}
          presetComputedMeasures={reportPresetComputedMeasures}
          presetCalculatedFields={reportPresetCalculatedFields}
          onSaveReport={handleSaveReport}
          onSaveSuccess={() => {
            const homeTab = tabs.find(t => t.menu === 'home')
            if (homeTab) {
              setCurrentMenu('home')
            } else {
              openOrActivateTab('home', '首页')
            }
          }}
        />
      ) : currentMenu === 'myspace' ? (
        <MySpace onOpenReport={handleOpenReport} onCreateNew={handleCreateNew} onEditReport={handleEditReport} onViewReport={handleNavigateToReportByTemplateId} onLearnMore={handleLearnMore} onCaseDetail={handleCaseDetailFromCases} onPayrollTopicAnalysis={handlePayrollTopicAnalysis} onNavigateToFavoritesTopics={handleNavToFavoritesTopics} onNavigateToFavoritesTemplates={handleNavToFavoritesTemplates} onNavigateToFavoritesCases={handleNavToFavoritesCases} version={appVersion} currentUser={currentUser} userBizRole={userBizRole} />
      ) : currentMenu === 'catalog' ? (
        <CatalogDetailPage
          catalogId={selectedCatalogDetailId || ''}
          onBack={handleBackFromCatalog}
          onReportQuery={handleReportQuery}
          authorized={catalogAuthorized}
        />
      ) : currentMenu === 'user-center' ? (
        <UserCenterPage
          onViewReport={handleNavigateToReport}
          onLearnMore={handleLearnMore}
          onCaseDetail={handleCaseDetailFromCases}
          onPayrollTopicAnalysis={handlePayrollTopicAnalysis}
          version={appVersion}
          currentUser={currentUser}
        />
      ) : currentMenu === 'admin-center' ? (
        <AdminCenterPage version={appVersion} />
      ) : null}
    </MainLayout>
  )
}

export default App
